import { FocusProspectFilterAPI } from '@business/api/focus_mode';
import {
  FocusProspectCategory,
  FocusProspectCustomField,
  FocusProspectFilterId,
  FocusProspectFilterRequest,
  FocusProspectFilterResponse,
  FocusProspectOwner,
  FocusProspectService,
  FocusProspectStatus,
  FocusProspectTag,
} from '@business/entities/focusMode';
import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { showDeleteConfirmation } from '@modules/common';
import { BehaviorSubject } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';

export class FocusProspectFilterBloc extends BaseBloc {
  private readonly filtersSubject = new BehaviorSubject<FocusProspectFilterResponse[]>([]);
  private readonly categoriesSubject = new BehaviorSubject<FocusProspectCategory[]>([]);
  private readonly servicesSubject = new BehaviorSubject<FocusProspectService[]>([]);
  // Owners, tags and statuses ride along on the same list fetch as categories and
  // services, so the modal never needs a round trip of its own.
  private readonly ownersSubject = new BehaviorSubject<FocusProspectOwner[]>([]);
  private readonly tagsSubject = new BehaviorSubject<FocusProspectTag[]>([]);
  private readonly statusesSubject = new BehaviorSubject<FocusProspectStatus[]>([]);
  // Organization dropdown custom fields, each carrying its own options — the only pick
  // list here that is nested rather than flat, because a condition needs both halves.
  private readonly customFieldsSubject = new BehaviorSubject<FocusProspectCustomField[]>([]);

  readonly filters$ = this.filtersSubject.asObservable();
  readonly categories$ = this.categoriesSubject.asObservable();
  readonly services$ = this.servicesSubject.asObservable();
  readonly owners$ = this.ownersSubject.asObservable();
  readonly tags$ = this.tagsSubject.asObservable();
  readonly statuses$ = this.statusesSubject.asObservable();
  readonly customFields$ = this.customFieldsSubject.asObservable();

  get filters() {
    return this.filtersSubject.value;
  }

  onReset() {
    this.filtersSubject.next([]);
    this.categoriesSubject.next([]);
    this.servicesSubject.next([]);
    this.ownersSubject.next([]);
    this.tagsSubject.next([]);
    this.statusesSubject.next([]);
    this.customFieldsSubject.next([]);
  }

  fetchFilters = () => {
    return FocusProspectFilterAPI.fetchProspectFilters().pipe(
      tap(response => {
        this.filtersSubject.next(response.filters ?? []);
        this.categoriesSubject.next(response.categories ?? []);
        this.servicesSubject.next(response.services ?? []);
        this.ownersSubject.next(response.owners ?? []);
        this.tagsSubject.next(response.tags ?? []);
        this.statusesSubject.next(response.statuses ?? []);
        this.customFieldsSubject.next(response.customFields ?? []);
      }),
      handleMessage({ error: requestMessage('fetch_general_error') }),
    );
  };

  createFilter = (data: FocusProspectFilterRequest) => {
    return FocusProspectFilterAPI.createProspectFilter(data).pipe(
      tap(r => this.filtersSubject.next([...this.filters, r.filter])),
      handleMessage({ type: requestMessage('focus_prospect_filter_saved') }),
    );
  };

  updateFilter = (data: FocusProspectFilterRequest) => {
    return FocusProspectFilterAPI.updateProspectFilter(data).pipe(
      tap(r => this.filtersSubject.next(this.filters.map(f => (f.id === r.filter.id ? r.filter : f)))),
      handleMessage({ type: requestMessage('focus_prospect_filter_saved') }),
    );
  };

  deleteFilter = (filter: FocusProspectFilterResponse) => {
    // Naming the assignment count rather than blocking the delete: an admin removing a
    // routed set should know who it was routed to before they confirm.
    const assigned = filter.assignedIds?.length ?? 0;
    const detail = assigned > 0 ? ` It is assigned to ${assigned} user${assigned === 1 ? '' : 's'}.` : '';

    return showDeleteConfirmation('Delete Filter Set', `Do you really want to remove "${filter.name}"?${detail}`).pipe(
      switchMap(() => FocusProspectFilterAPI.deleteProspectFilter(filter.id)),
      tap(() => this.filtersSubject.next(this.filters.filter(f => f.id !== filter.id))),
      handleMessage({ type: requestMessage('focus_prospect_filter_deleted') }),
    );
  };

  saveOrder = (ids: FocusProspectFilterId[]) => {
    return FocusProspectFilterAPI.saveProspectFilterOrder(ids).pipe(
      tap(response => this.filtersSubject.next(response.filters ?? [])),
      handleMessage({ error: requestMessage('focus_prospect_filter_order') }),
    );
  };
}
